import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Modal, Badge } from 'react-bootstrap';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiPhone, FiLock, FiEye, FiEyeOff, FiSave, FiArrowLeft, FiShield } from 'react-icons/fi';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { isValidPhone } from '../../common/validators';

const StaffDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState(null);
  const [roles, setRoles] = useState([]);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    roleId: '',
    status: 'active',
    telegramId: ''
  });

  // Password change states
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, rolesRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/staff/${id}`),
        axios.get(`${config.API_BASE_URL}/roles`)
      ]);
      
      const staffData = staffRes.data;
      setStaff(staffData);
      setRoles(rolesRes.data);
      
      setFormData({
        name: staffData.name || '',
        email: staffData.email || '',
        phone: staffData.phone || '',
        roleId: staffData.roleId?._id || staffData.roleId || '',
        status: staffData.status || 'active',
        telegramId: staffData.telegramId || ''
      });
    } catch (error) {
      console.error("Error fetching staff details", error);
      toast.error("Failed to load staff details");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.name) { toast.error("Name is required"); return false; }
    if (!formData.email) { toast.error("Email is required"); return false; }
    if (!isValidPhone(formData.phone)) { toast.error("Invalid phone number"); return false; }
    
    if (passwordData.newPassword) {
      if (passwordData.newPassword.length < 6) {
        toast.error("Password must be at least 6 characters");
        return false;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error("Passwords do not match");
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setShowConfirmModal(true);
    }
  };

  const confirmUpdate = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    try {
      const payload = { ...formData };
      if (passwordData.newPassword) {
        payload.password = passwordData.newPassword;
      }
      
      await axios.put(`${config.API_BASE_URL}/staff/${id}`, payload);
      toast.success("Staff details updated successfully");
      setPasswordData({ newPassword: '', confirmPassword: '' });
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Update failed", error);
      toast.error(error.response?.data?.message || "Failed to update staff");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Staff Details">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading staff profile...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Staff: ${staff?.name}`}>
      <Container fluid className="p-4">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/staff">Staff List</Link></li>
            <li className="breadcrumb-item active">{staff?.name}</li>
          </ol>
        </nav>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">{staff?.name}</h2>
            <p className="text-muted mb-0">Manage profile details and security</p>
          </div>
          <Button variant="outline-secondary" onClick={() => navigate('/staff')}>
            <FiArrowLeft className="me-2" /> Back to List
          </Button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              {/* Basic Details Card */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white py-3">
                  <h5 className="mb-0"><FiUser className="me-2" />Basic Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control 
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control 
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleFormChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Phone Number</Form.Label>
                        <PhoneInput
                          country="in"
                          value={formData.phone}
                          onChange={(val) => setFormData(prev => ({ ...prev, phone: `+${val}` }))}
                          inputClass="w-100"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Role</Form.Label>
                        <Form.Select 
                          name="roleId"
                          value={formData.roleId}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">Select Role</option>
                          {roles.map(r => (
                            <option key={r._id} value={r._id}>{r.role_name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Telegram User ID</Form.Label>
                        <Form.Control 
                          name="telegramId"
                          value={formData.telegramId}
                          onChange={handleFormChange}
                          placeholder="Optional: For Telegram notifications"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Security Card */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white py-3">
                  <h5 className="mb-0"><FiShield className="me-2" />Security & Password</h5>
                </Card.Header>
                <Card.Body>
                  <Alert variant="info" className="mb-4">
                    <FiLock className="me-2" />
                    Leave these fields blank if you do not wish to change the password.
                  </Alert>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>New Password</Form.Label>
                        <div className="position-relative">
                          <Form.Control 
                            type={showPassword ? "text" : "password"}
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            placeholder="Min 6 characters"
                          />
                          <Button 
                            variant="link" 
                            className="position-absolute end-0 top-50 translate-middle-y text-muted"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Confirm New Password</Form.Label>
                        <Form.Control 
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="border-0 shadow-sm sticky-top" style={{ top: '100px' }}>
                <Card.Body className="text-center py-4">
                  <div className="mb-3">
                    <img 
                      src="/assets/img/team/default-avatar.png" 
                      alt="avatar" 
                      className="rounded-circle shadow-sm"
                      width="120"
                    />
                  </div>
                  <h4 className="mb-1">{staff.name}</h4>
                  <Badge bg="soft-primary" className="text-primary mb-3">
                    {staff.roleId?.name || 'No Role'}
                  </Badge>
                  <hr />
                  <div className="text-start mb-4">
                    <div className="d-flex align-items-center mb-2 text-muted">
                      <FiMail className="me-2" /> {staff.email}
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <FiPhone className="me-2" /> {staff.phone}
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-100 py-2"
                    disabled={saving}
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : <><FiSave className="me-2" /> Save Changes</>}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to update the staff details? 
          {passwordData.newPassword && <div className="text-danger mt-2 fw-bold">Note: This will also change the user's password.</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={confirmUpdate}>Confirm & Update</Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default StaffDetail;
